import UUID from "../../Generic/UUID";
import { ApplicationCommunicator, type ApplicationMessageHandlerData } from "./ApplicationCommunicator";

export class ApplicationJavaScriptCommunicator extends ApplicationCommunicator {
    protected currentId = "";
    protected module: null | {
        onReceiveMessage: (type: string, data: unknown, id?: string) => void;
    } = null;

    /**
     * Receive a message from the connected module
     */
    protected handleMessageFromModule(message: unknown, id: string): void {
        if(id !== this.currentId) {
            return;
        }

        if(typeof message !== "object" || message === null) {
            return;
        }

        if(!("key" in message) || typeof message.key !== "string") {
            return;
        }

        if(!("type" in message) || typeof message.type !== "string") {
            return;
        }

        this.handleMessage(message as ApplicationMessageHandlerData);
    }

    /**
     * Setup and create the communication channel
     */
    public async create(): Promise<void> {
        let module;

        try {
            module = await import(/* webpackIgnore: true */ this.url);
        } catch(e) {
            console.error(`Unable to load module ${this.getName()}`, e);

            return;
        }

        const requiredFunctions = [
            "execute",
            "registerSendMessage",
            "onReceiveMessage",
        ];

        // Check if we've just provided the application as the default export, if so, use the communicator as the module
        // instead
        if(
            "default" in module &&
            typeof module.default === "object" &&
            module.default !== null &&
            "communicator" in module.default &&
            typeof module.default.communicator === "object" &&
            module.default.communicator !== null
        ) {
            module = module.default.communicator;
        }

        // Loop through all the required functions to ensure the module contains them
        for(const fnName of requiredFunctions) {
            if(!(fnName in module) || typeof module[fnName] !== "function") {
                console.error(
                    `The \`${fnName}\` method on the ${this.getName()} application isn't implemented correctly`
                );

                return;
            }
        }

        const moduleId = UUID.generate();

        this.currentId = moduleId;

        try {
            module.registerSendMessage((message: unknown) => this.handleMessageFromModule(message, moduleId));
        } catch(e) {
            this.currentId = "";
            console.error(
                `The ${this.getName()} application encountered an error while registering the send message function`,
                e
            );

            return;
        }

        this.module = module;

        try {
            await module.execute();
        } catch(e) {
            this.module = null;
            this.currentId = "";
            console.error(
                `The ${this.getName()} application encountered an error while performing initial execution`,
                e
            );
        }
    }

    /**
     * Remove the communication channel with the application
     */
    public async destroy(): Promise<void> {
        this.currentId = "";
        this.module = null;
    }

    /**
     * Update the communication channel with new information from a sync
     */
    public async update(url: string): Promise<void> {
        this.url = url;

        await this.create();
    }

    /**
     * Send a message to the application we're communicating with, returns true on success, false otherwise
     */
    public sendMessage(type: string, data: unknown, id?: string): boolean {
        if(!this.module || !this.currentId) {
            return false;
        }

        try {
            this.module.onReceiveMessage(type, data, id);
        } catch(e) {
            console.error(`Received an error while sending a message to ${this.getName()}`, type, data, id);

            return false;
        }

        return true;
    }

    /**
     * Check if the communicator is ready for communication
     */
    public isReady(): boolean {
        return this.module !== null && this.currentId !== "";
    }

    /**
     * @inheritDoc
     */
    public getRawUrl(): URL | string {
        return this.url;
    }

    /**
     * @inheritDoc
     */
    public getLoadedUrl(): URL | string {
        return this.url;
    }

    /**
     * Because of the way events work when we're using the JavqScript communicator, we know anything external isn't
     * from here
     */
    public isEventFromSource(): boolean {
        return false;
    }
}
